import {
  MasterBlockId,
  PageEntityInterface,
} from 'src/shared/types/base-entity-interface'
import {
  ReduxLoggingActionTypes,
  ADD_REDUX_LOG,
  SEND_REDUX_LOGS_TO_ROLLBAR,
  SET_REDUX_LOG_DATA,
  RESET_REDUX_LOG_DATA,
  RESET_ALL_REDUX_LOG_DATA,
} from './redux-logging-action-types'
import { limitArraySize } from 'src/shared/utils/limit-array-size'

const MAX_LOGS_SIZE = 200

export enum ReduxLoggingTypeEnum {
  // NOTE: Logging for root master block entities that lost masterBlockId during page editing
  ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID = 'ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID',
  // TODO: Add logging for root master block entities that lost isMasterBlockRoot during page editing
  // ROOT_MASTER_BLOCK_ENTITY_LOST_IS_MASTER_BLOCK_ROOT = 'ROOT_MASTER_BLOCK_ENTITY_LOST_IS_MASTER_BLOCK_ROOT',
}

export type ReduxLogsType = string[]

export interface CommonReduxLoggingInterface {
  shouldBeSentToRollbar?: boolean
}

export type RootMasterBlockEntitiesLostMasterBlockIdLogDataType = Record<
  MasterBlockId,
  {
    masterBlockId: MasterBlockId
    rootEntity?: PageEntityInterface
  } & CommonReduxLoggingInterface
>

export type ReduxLoggingState = {
  logData: {
    [ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID]?: RootMasterBlockEntitiesLostMasterBlockIdLogDataType
  }
  logs: ReduxLogsType
}

const defaultState: ReduxLoggingState = {
  logData: {},
  logs: [],
}

export const reduxLoggingReducer = <
  ReduxLoggingType extends ReduxLoggingTypeEnum,
>(
  state: ReduxLoggingState = defaultState,
  action: ReduxLoggingActionTypes<ReduxLoggingType>,
): ReduxLoggingState => {
  const { type, payload } = action

  switch (type) {
    case ADD_REDUX_LOG: {
      const limitedLogs = limitArraySize(
        [...state.logs, payload],
        MAX_LOGS_SIZE,
      )
      return {
        ...state,
        logs: limitedLogs,
      }
    }
    case SET_REDUX_LOG_DATA:
      switch (payload.logType) {
        case ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID:
          return {
            ...state,
            logData: {
              ...state.logData,
              [payload.logType]: {
                ...state.logData[payload.logType],
                [payload.data.masterBlockId]: {
                  ...state.logData[payload.logType]?.[
                    payload.data.masterBlockId
                  ],
                  ...payload.data,
                },
              },
            },
          }
        default:
          return state
      }
    case SEND_REDUX_LOGS_TO_ROLLBAR:
      switch (payload.logType) {
        case ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID:
          return {
            ...state,
            logData: {
              ...state.logData,
              [payload.logType]: {
                ...state.logData[payload.logType],
                [payload.data.masterBlockId]: {
                  ...state.logData[payload.logType]?.[
                    payload.data.masterBlockId
                  ],
                  ...payload.data,
                  shouldBeSentToRollbar: true,
                },
              },
            },
          }
        default:
          return state
      }
    case RESET_REDUX_LOG_DATA:
      switch (payload.logType) {
        case ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID: {
          const { [payload.data.masterBlockId]: _, ...remainingLogTypeData } =
            state.logData[payload.logType] || {}
          return {
            ...state,
            logData: {
              ...state.logData,
              [payload.logType]: remainingLogTypeData,
            },
          }
        }
        default:
          return state
      }
    case RESET_ALL_REDUX_LOG_DATA: {
      const { [payload.logType]: _, ...remainingLogData } = state.logData || {}
      return {
        ...state,
        logData: remainingLogData,
      }
    }
    default:
      return state
  }
}
